"use client";

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

type CookieSetOptions = {
  /** Cookie lifetime in seconds. Defaults to one year. */
  maxAgeSeconds?: number;
};

type CookiesContextValue = {
  /** Current cookie values (server-seeded, then kept in sync by {@link setCookie}). */
  cookies: Record<string, string>;
  /** Persist a cookie (path=/, SameSite=Lax) and update every consumer. */
  setCookie: (name: string, value: string, options?: CookieSetOptions) => void;
};

const CookiesContext = createContext<CookiesContextValue | null>(null);

const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365;

/**
 * Client cookie store backing {@link useCookieState}. Seed `initialCookies`
 * from the server (`next/headers` in the root layout) so the first render
 * already knows cookie state — no post-hydration flash. Only allowlisted,
 * non-httpOnly cookies should be seeded (see `constants/cookies.ts`).
 */
export const CookiesProvider = ({
  initialCookies = {},
  children,
}: {
  initialCookies?: Record<string, string>;
  children: ReactNode;
}) => {
  const [cookies, setCookies] = useState<Record<string, string>>(initialCookies);

  const setCookie = useCallback(
    (name: string, value: string, { maxAgeSeconds = ONE_YEAR_SECONDS }: CookieSetOptions = {}) => {
      document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; path=/; max-age=${maxAgeSeconds}; SameSite=Lax`;
      setCookies((prev) => ({ ...prev, [name]: value }));
    },
    [],
  );

  const value = useMemo(() => ({ cookies, setCookie }), [cookies, setCookie]);

  return <CookiesContext.Provider value={value}>{children}</CookiesContext.Provider>;
};

export const useCookiesContext = (): CookiesContextValue => {
  const context = useContext(CookiesContext);
  if (!context) throw new Error("useCookieState must be used within a CookiesProvider");
  return context;
};
